import * as React from 'react';
import styled, { css } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { Presence } from '../../internals/Presence';
import { useControllable } from '../../internals/useControllable';
import { useScrollLock } from '../../internals/useScrollLock';
import { palette } from '../../tokens/palette';
import { rem } from '../../tokens/spacing';
import { zIndex } from '../../tokens/zIndex';

import { NavbarProvider, useNavbarContext } from './NavbarContext';
import {
  NavbarNavDropdown,
  NavbarNavDropdownItem,
} from './NavbarDropdown';
import {
  NavbarProfileMenu,
  NavbarProfileMenuItem,
  NavbarProfileMenuDivider,
} from './NavbarProfileMenu';
import {
  NavbarMobileAccordion,
  NavbarMobileAccordionItem,
} from './NavbarMobileAccordion';
import {
  NavbarNotificationBell,
  NavbarInstanceSwitch,
  NavbarUserTrigger,
  NavbarSpacer,
} from './NavbarActionPrimitives';
import { MegaMenu } from './NavbarMegaMenu';

export { useNavbarContext } from './NavbarContext';
export type { NavbarContextValue } from './NavbarContext';

/* ─────────────────────────── Types ────────────────────────────────────── */

export type NavbarPosition = 'sticky' | 'fixed';
export type NavbarVariant = 'default' | 'sandbox' | 'switched';

/* ─────────────────────────── Root ──────────────────────────────────────── */

export interface NavbarRootProps extends React.HTMLAttributes<HTMLElement> {
  position?: NavbarPosition;
  /** Offset from top (e.g., for a notification banner above). */
  topOffset?: string;
  /** Visual variant for the bar background. */
  variant?: NavbarVariant;
  /** Controls the mobile menu. */
  mobileOpen?: boolean;
  defaultMobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
  semanticId?: string;
}

const VARIANT_BG: Record<NavbarVariant, string> = {
  default: palette.White,
  sandbox: palette.CornSilk,
  switched: palette.PinkLace,
};

const TRANSIENT_LAYOUT = ['$navbarH', '$noticeH'] as const;
const shouldForwardLayout = (prop: string | number): boolean =>
  !TRANSIENT_LAYOUT.includes(prop as (typeof TRANSIENT_LAYOUT)[number]);

const StyledNavbarLayout = styled.div.withConfig({ shouldForwardProp: shouldForwardLayout })<{
  $navbarH: string;
  $noticeH: string;
}>`
  --ui-navbar-h: ${({ $navbarH }) => $navbarH};
  --ui-notice-h: ${({ $noticeH }) => $noticeH};
  --ui-navbar-offset: calc(var(--ui-navbar-h) + var(--ui-notice-h));
  display: contents;
`;

const TRANSIENT_ROOT = ['$position', '$variant'] as const;
const shouldForwardRoot = (prop: string | number): boolean =>
  !TRANSIENT_ROOT.includes(prop as (typeof TRANSIENT_ROOT)[number]);

const StyledNav = styled.nav.withConfig({ shouldForwardProp: shouldForwardRoot })<{
  $position: NavbarPosition;
  $variant: NavbarVariant;
}>`
  height: var(--ui-navbar-h, ${rem(67)});
  width: 100vw;
  position: ${({ $position }) => $position};
  top: var(--ui-notice-h, 0px);
  left: 0;
  z-index: ${zIndex.navbar};
  background: ${({ $variant }) => VARIANT_BG[$variant]};
  box-shadow: 0 4px 6px -1px var(--ui-navbar-shadow, rgba(0, 0, 0, 0.1)), 0 2px 4px -2px var(--ui-navbar-shadow, rgba(0, 0, 0, 0.1));
  padding: 0 ${rem(20)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  -webkit-font-smoothing: antialiased;
  box-sizing: border-box;
`;

const DESKTOP_BREAKPOINT = 992;

const NavbarRoot = React.forwardRef<HTMLElement, NavbarRootProps>(function NavbarRoot(
  {
    position = 'fixed',
    topOffset = '0px',
    variant = 'default',
    mobileOpen,
    defaultMobileOpen,
    onMobileOpenChange,
    semanticId,
    children,
    ...rest
  },
  ref,
) {
  const [isOpen, setOpen] = useControllable<boolean>({
    value: mobileOpen,
    defaultValue: defaultMobileOpen ?? false,
    onChange: onMobileOpenChange,
  });

  const [openDropdownId, setOpenDropdownId] = React.useState<string | null>(null);

  useScrollLock(isOpen);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => {
      if (window.innerWidth >= DESKTOP_BREAKPOINT && isOpen) {
        setOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, setOpen]);

  const ctx = React.useMemo(
    () => ({
      mobileOpen: isOpen,
      onMobileOpenChange: setOpen,
      openDropdownId,
      setOpenDropdownId,
    }),
    [isOpen, setOpen, openDropdownId],
  );
  const semanticProps = getSemanticAttributes(semanticId);

  return (
    <NavbarProvider value={ctx}>
      <StyledNavbarLayout $navbarH={rem(67)} $noticeH={topOffset}>
        <StyledNav
          ref={ref}
          $position={position}
          $variant={variant}
          {...semanticProps}
          {...rest}
        >
          {children}
        </StyledNav>
      </StyledNavbarLayout>
    </NavbarProvider>
  );
});

NavbarRoot.displayName = 'Navbar.Root';

/**
 * Wrapper that provides NavbarProvider context for pieces rendered
 * outside `<Navbar.Root>` (e.g. MobileBackdrop, MobileMenu).
 * Use this when the mobile overlay sits as a sibling to Root in the tree.
 */
export interface NavbarShellProps extends NavbarRootProps {
  /** Content rendered *outside* the nav bar but still needing context (MobileBackdrop, MobileMenu). */
  overlay?: React.ReactNode;
}

const NavbarShell = React.forwardRef<HTMLElement, NavbarShellProps>(function NavbarShell(
  { overlay, children, ...rootProps },
  ref,
) {
  const [isOpen, setOpen] = useControllable<boolean>({
    value: rootProps.mobileOpen,
    defaultValue: rootProps.defaultMobileOpen ?? false,
    onChange: rootProps.onMobileOpenChange,
  });

  const [openDropdownId, setOpenDropdownId] = React.useState<string | null>(null);

  useScrollLock(isOpen);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => {
      if (window.innerWidth >= DESKTOP_BREAKPOINT && isOpen) {
        setOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, setOpen]);

  const ctx = React.useMemo(
    () => ({
      mobileOpen: isOpen,
      onMobileOpenChange: setOpen,
      openDropdownId,
      setOpenDropdownId,
    }),
    [isOpen, setOpen, openDropdownId],
  );

  const {
    position = 'fixed',
    topOffset = '0px',
    variant = 'default',
    semanticId,
    mobileOpen: _mo,
    defaultMobileOpen: _dmo,
    onMobileOpenChange: _omoc,
    ...navRest
  } = rootProps;
  const semanticProps = getSemanticAttributes(semanticId);

  return (
    <NavbarProvider value={ctx}>
      <StyledNavbarLayout $navbarH={rem(67)} $noticeH={topOffset}>
        <StyledNav
          ref={ref}
          $position={position}
          $variant={variant}
          {...semanticProps}
          {...navRest}
        >
          {children}
        </StyledNav>
        {overlay}
      </StyledNavbarLayout>
    </NavbarProvider>
  );
});

NavbarShell.displayName = 'Navbar.Shell';

/* ─────────────────────────── Brand ────────────────────────────────────── */

export interface NavbarBrandProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const StyledBrand = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: 100%;

  img {
    min-width: ${rem(100)};
    max-width: min(191px, 90%);
    max-height: ${rem(45)};
    object-fit: contain;
    padding: 0 ${rem(10)};
  }
`;

const NavbarBrand = React.forwardRef<HTMLDivElement, NavbarBrandProps>(function NavbarBrand(
  { semanticId, ...rest },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);
  return <StyledBrand ref={ref} {...semanticProps} {...rest} />;
});

NavbarBrand.displayName = 'Navbar.Brand';

/* ─────────────────────────── Nav ──────────────────────────────────────── */

export interface NavbarNavProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const StyledNavMenu = styled.div`
  display: none;
  justify-content: center;
  align-items: center;
  gap: ${rem(2)};
  height: 100%;

  @media (min-width: 992px) {
    display: flex;
    gap: ${rem(2)};
  }

  @media (min-width: 1200px) {
    gap: ${rem(10)};
  }
`;

const NavbarNav = React.forwardRef<HTMLDivElement, NavbarNavProps>(function NavbarNav(
  { semanticId, ...rest },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);
  return <StyledNavMenu ref={ref} {...semanticProps} {...rest} />;
});

NavbarNav.displayName = 'Navbar.Nav';

/* ─────────────────────────── NavItem ──────────────────────────────────── */

export interface NavbarNavItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Highlights the item with an active underline. */
  active?: boolean;
  semanticId?: string;
}

const TRANSIENT_ITEM = ['$active'] as const;
const shouldForwardItem = (prop: string | number): boolean =>
  !TRANSIENT_ITEM.includes(prop as (typeof TRANSIENT_ITEM)[number]);

const navItemChildStyles = css`
  font-weight: 400;
  font-size: ${rem(16)};
  line-height: ${rem(22)};
  color: var(--ui-text, ${palette.Black});
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  height: 100%;
  position: relative;
  transition: color 0.2s linear;
  font-family: inherit;
  padding: 0;

  &::after {
    content: '';
    height: 3px;
    width: 0;
    background: var(--ui-interactive, ${palette.Purple});
    position: absolute;
    left: 0;
    bottom: 0;
    transition: width 0.2s linear;
  }

  &:hover::after {
    width: 100%;
  }
`;

const navItemActiveStyles = css`
  color: var(--ui-interactive, ${palette.Purple});
  &::after { width: 100%; }
`;

const StyledNavItem = styled.div.withConfig({ shouldForwardProp: shouldForwardItem })<{
  $active: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  height: var(--ui-navbar-h, ${rem(67)});
  padding: 0 ${rem(5)};
  cursor: pointer;

  > a,
  > button,
  > span {
    ${navItemChildStyles}
  }

  ${({ $active }) =>
    $active &&
    css`
      > a,
      > button,
      > span {
        ${navItemActiveStyles}
      }
    `}
`;

const StyledNavItemFallback = styled.span`
  ${navItemChildStyles}
`;

const StyledNavItemFallbackActive = styled.span`
  ${navItemChildStyles}
  ${navItemActiveStyles}
`;

function needsFallbackWrap(children: React.ReactNode): boolean {
  return React.Children.toArray(children).every(
    (child) => typeof child === 'string' || typeof child === 'number',
  );
}

const NavbarNavItem = React.forwardRef<HTMLDivElement, NavbarNavItemProps>(
  function NavbarNavItem({ active = false, semanticId, children, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    const wrapped = needsFallbackWrap(children)
      ? active
        ? <StyledNavItemFallbackActive>{children}</StyledNavItemFallbackActive>
        : <StyledNavItemFallback>{children}</StyledNavItemFallback>
      : children;
    return (
      <StyledNavItem
        ref={ref}
        $active={active}
        data-active={active || undefined}
        {...semanticProps}
        {...rest}
      >
        {wrapped}
      </StyledNavItem>
    );
  },
);

NavbarNavItem.displayName = 'Navbar.NavItem';

/* ─────────────────────────── Actions ──────────────────────────────────── */

export interface NavbarActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const StyledActions = styled.div`
  display: none;
  align-items: center;
  gap: ${rem(7)};
  flex-shrink: 0;
  height: 100%;

  @media (min-width: 992px) {
    display: flex;
  }
`;

const NavbarActions = React.forwardRef<HTMLDivElement, NavbarActionsProps>(
  function NavbarActions({ semanticId, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    return <StyledActions ref={ref} {...semanticProps} {...rest} />;
  },
);

NavbarActions.displayName = 'Navbar.Actions';

/* ─────────────────────────── MobileToggle ────────────────────────────── */

export interface NavbarMobileToggleProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  semanticId?: string;
}

const StyledMobileToggle = styled.button`
  display: flex;
  align-items: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: ${rem(7)} ${rem(12)};
  border-radius: var(--ui-radius-full, ${rem(30)});
  font-family: inherit;
  font-size: ${rem(14)};
  color: var(--ui-text, ${palette.Black});

  .material-symbols-outlined {
    font-size: ${rem(20)};
    padding-right: ${rem(7)};
  }

  @media (min-width: 992px) {
    display: none;
  }
`;

const NavbarMobileToggle = React.forwardRef<HTMLButtonElement, NavbarMobileToggleProps>(
  function NavbarMobileToggle({ semanticId, onClick, children, ...rest }, ref) {
    const { mobileOpen, onMobileOpenChange } = useNavbarContext();
    const semanticProps = getSemanticAttributes(semanticId);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      if (!e.defaultPrevented) onMobileOpenChange(!mobileOpen);
    };

    return (
      <StyledMobileToggle
        ref={ref}
        type="button"
        onClick={handleClick}
        aria-expanded={mobileOpen}
        {...semanticProps}
        {...rest}
      >
        {children ?? (
          <>
            <span className="material-symbols-outlined">
              {mobileOpen ? 'close' : 'menu'}
            </span>
            Menu
          </>
        )}
      </StyledMobileToggle>
    );
  },
);

NavbarMobileToggle.displayName = 'Navbar.MobileToggle';

/* ─────────────────────────── MobileBackdrop ──────────────────────────── */

export interface NavbarMobileBackdropProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const StyledBackdrop = styled.div`
  position: fixed;
  top: var(--ui-navbar-offset, ${rem(67)});
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${zIndex.navbarBackdrop};
  opacity: 0;
  transition: opacity 0.3s ease;

  &[data-state='open'] {
    opacity: 1;
  }

  @media (min-width: 992px) {
    display: none;
  }
`;

const NavbarMobileBackdrop = React.forwardRef<HTMLDivElement, NavbarMobileBackdropProps>(
  function NavbarMobileBackdrop({ semanticId, onClick, ...rest }, ref) {
    const { mobileOpen, onMobileOpenChange } = useNavbarContext();
    const semanticProps = getSemanticAttributes(semanticId);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      onClick?.(e);
      if (!e.defaultPrevented) onMobileOpenChange(false);
    };

    return (
      <Presence present={mobileOpen} animateEnter>
        <StyledBackdrop
          ref={ref}
          onClick={handleClick}
          {...semanticProps}
          {...rest}
        />
      </Presence>
    );
  },
);

NavbarMobileBackdrop.displayName = 'Navbar.MobileBackdrop';

/* ─────────────────────────── MobileMenu ──────────────────────────────── */

export interface NavbarMobileMenuProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const StyledMobileMenu = styled.div`
  display: flex;
  flex-direction: column;
  position: fixed;
  width: 280px;
  max-width: 85vw;
  height: calc(100vh - var(--ui-navbar-offset, ${rem(67)}));
  background: var(--ui-surface, ${palette.OffWhite});
  top: var(--ui-navbar-offset, ${rem(67)});
  right: 0;
  z-index: ${zIndex.navbarMobile};
  overflow-y: auto;
  transform: translateX(100%);
  transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &[data-state='open'] {
    transform: translateX(0);
  }

  @media (min-width: 480px) and (max-width: 767px) {
    width: 320px;
  }

  @media (min-width: 768px) and (max-width: 991px) {
    width: 400px;
  }

  @media (min-width: 992px) {
    display: none;
  }
`;

const NavbarMobileMenu = React.forwardRef<HTMLDivElement, NavbarMobileMenuProps>(
  function NavbarMobileMenu({ semanticId, children, ...rest }, ref) {
    const { mobileOpen } = useNavbarContext();
    const semanticProps = getSemanticAttributes(semanticId);

    return (
      <Presence present={mobileOpen} animateEnter>
        <StyledMobileMenu ref={ref} {...semanticProps} {...rest}>
          {children}
        </StyledMobileMenu>
      </Presence>
    );
  },
);

NavbarMobileMenu.displayName = 'Navbar.MobileMenu';

/* ─────────────────────────── MobileNavItem ────────────────────────────── */

export interface NavbarMobileNavItemProps extends React.HTMLAttributes<HTMLDivElement> {
  active?: boolean;
  semanticId?: string;
}

const TRANSIENT_MOBILE_ITEM = ['$active'] as const;
const shouldForwardMobileItem = (prop: string | number): boolean =>
  !TRANSIENT_MOBILE_ITEM.includes(prop as (typeof TRANSIENT_MOBILE_ITEM)[number]);

const StyledMobileNavItem = styled.div.withConfig({
  shouldForwardProp: shouldForwardMobileItem,
})<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  text-align: left;
  width: 100%;
  align-items: flex-start;
  padding: ${rem(16)} ${rem(20)};
  border-bottom: 1px solid rgba(215, 215, 215, 0.5);

  > a,
  > button,
  > span {
    font-weight: 600;
    font-size: ${rem(15)};
    line-height: ${rem(19)};
    color: var(--ui-text, ${palette.Black});
    display: flex;
    align-items: center;
    cursor: pointer;
    text-decoration: none;
    background: none;
    border: none;
    font-family: inherit;
    padding: 0;
    width: 100%;
    transition: color 0.2s linear;
  }

  ${({ $active }) =>
    $active &&
    css`
      > a,
      > button,
      > span {
        background: var(--ui-interactive, ${palette.Purple});
        color: ${palette.White};
        padding: ${rem(8)} ${rem(10)};
        border-radius: var(--ui-radius-md, ${rem(8)});
      }
    `}
`;

const NavbarMobileNavItem = React.forwardRef<HTMLDivElement, NavbarMobileNavItemProps>(
  function NavbarMobileNavItem({ active = false, semanticId, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    return (
      <StyledMobileNavItem
        ref={ref}
        $active={active}
        data-active={active || undefined}
        {...semanticProps}
        {...rest}
      />
    );
  },
);

NavbarMobileNavItem.displayName = 'Navbar.MobileNavItem';

/* ─────────────────────────── MobileMenuSection ─────────────────────────── */

export interface NavbarMobileMenuSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Optional label rendered at the top of the section. */
  label?: string;
  semanticId?: string;
}

const StyledMobileMenuSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledSectionLabel = styled.div`
  font-size: ${rem(11)};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(119, 119, 119, 0.6);
  padding: ${rem(20)} ${rem(20)} ${rem(6)};
  margin-bottom: ${rem(4)};
`;

const NavbarMobileMenuSection = React.forwardRef<HTMLDivElement, NavbarMobileMenuSectionProps>(
  function NavbarMobileMenuSection({ label, semanticId, children, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    return (
      <StyledMobileMenuSection ref={ref} role="group" aria-label={label} {...semanticProps} {...rest}>
        {label != null && <StyledSectionLabel>{label}</StyledSectionLabel>}
        {children}
      </StyledMobileMenuSection>
    );
  },
);

NavbarMobileMenuSection.displayName = 'Navbar.MobileMenuSection';

/* ─────────────────────────── Notice Bar ──────────────────────────────── */

export interface NavbarNoticeProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Called when the user clicks the dismiss button. */
  onDismiss?: () => void;
  semanticId?: string;
}

const StyledNotice = styled.div`
  background-color: var(--ui-surface-muted, ${palette.CulturedWhite});
  padding: 0 ${rem(20)};
  min-height: var(--ui-notice-h, ${rem(34)});
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: ${rem(48)};
  position: fixed;
  z-index: ${zIndex.navbarBackdrop};
  width: 100%;
  top: 0;
  transition: all 0.2s linear;

  .notice-close {
    color: var(--ui-text-subtle, ${palette.SpanishGray});
    position: absolute;
    top: 50%;
    right: ${rem(24)};
    transform: translateY(-50%);
    cursor: pointer;
    background: none;
    border: none;
    font-size: inherit;
    display: flex;
    align-items: center;
  }
`;

const NavbarNotice = React.forwardRef<HTMLDivElement, NavbarNoticeProps>(
  function NavbarNotice({ onDismiss, semanticId, children, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    return (
      <StyledNotice ref={ref} {...semanticProps} {...rest}>
        {children}
        {onDismiss != null && (
          <button type="button" className="notice-close" onClick={onDismiss} aria-label="Dismiss">
            <span className="material-symbols-outlined">close</span>
          </button>
        )}
      </StyledNotice>
    );
  },
);

NavbarNotice.displayName = 'Navbar.Notice';

/* ─────────────────────────── Compound export ──────────────────────────── */

export const Navbar = {
  Root: NavbarRoot,
  Shell: NavbarShell,
  Brand: NavbarBrand,
  Nav: NavbarNav,
  NavItem: NavbarNavItem,
  NavDropdown: NavbarNavDropdown,
  NavDropdownItem: NavbarNavDropdownItem,
  Actions: NavbarActions,
  MobileToggle: NavbarMobileToggle,
  MobileBackdrop: NavbarMobileBackdrop,
  MobileMenu: NavbarMobileMenu,
  MobileMenuSection: NavbarMobileMenuSection,
  MobileNavItem: NavbarMobileNavItem,
  MobileAccordion: NavbarMobileAccordion,
  MobileAccordionItem: NavbarMobileAccordionItem,
  ProfileMenu: NavbarProfileMenu,
  ProfileMenuItem: NavbarProfileMenuItem,
  ProfileMenuDivider: NavbarProfileMenuDivider,
  NotificationBell: NavbarNotificationBell,
  InstanceSwitch: NavbarInstanceSwitch,
  UserTrigger: NavbarUserTrigger,
  Spacer: NavbarSpacer,
  Notice: NavbarNotice,
  MegaMenu,
};
